/* global chrome */
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './Settings.css';
import { useTranslation } from 'react-i18next';
import countriesLocation from '../../components/CountriesLocation';
import { useNotification } from './../../components/NotificationContext';
import Userfront from '../../auth/userfrontCompat';
import api from './../../api/axios';
import { Modal, Button, Form } from 'react-bootstrap';
import { Tooltip } from 'bootstrap';
import { useBlocker } from '../../utils/usePrompt'; // Adjust path as needed
import IntegrationsView from '../../components/integrations/IntegrationsView';
import ChannelConnections from '../../components/ChannelConnections';


function Settings() {
  const { showNotification, removeNotification } = useNotification();
  const location = useLocation();
  const user = Userfront.user.email;
  const { i18n, t } = useTranslation();

  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const nextPath = useRef(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [connectionLimit, setConnectionLimit] = useState(0);
  const [profileViewLimit, setProfileViewLimit] = useState(0);
  const [messagesLimit, setMessagesLimit] = useState(0);
  const [profileImage, setProfileImage] = useState('');
  const [country, setCountry] = useState('');
  const [currentUser, setCurrentUser] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [language, setLanguage] = useState('');
  const [activeTab, setActiveTab] = useState('account');
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({ newConnection: false, newMessage: false });
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savedGeneral, setSavedGeneral] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [savedLocation, setSavedLocation] = useState(false);
  const [savingCookie, setSavingCookie] = useState(false);
  const [savedCookie, setSavedCookie] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const [savedLimits, setSavedLimits] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [savedNotifications, setSavedNotifications] = useState(false);
  const [networkHealth, setNetworkHealth] = useState(null);
  

  const [show2FAModal, setShow2FAModal] = useState(false);
const [waitingForPin, setWaitingForPin] = useState(true);
const [pin, setPin] = useState('');
const [sessionId, setSessionId] = useState(null);





  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    setLanguage(newLanguage);
    setUnsavedChanges(true);
  };

  const handleChangeWithTracking = (setter) => (value) => {
    setter(value);
    setUnsavedChanges(true);
  };

  useBlocker(
  (tx) => {
    nextPath.current = tx;
    setShowUnsavedModal(true);
  },
  unsavedChanges
);

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    [...tooltipTriggerList].map(tooltipTriggerEl => new Tooltip(tooltipTriggerEl));
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedChanges]);

  const confirmNavigation = () => {
    setShowUnsavedModal(false);
    setUnsavedChanges(false);
    nextPath.current?.retry();
  };

  const cancelNavigation = () => {
    setShowUnsavedModal(false);
    nextPath.current = null;
  };

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    [...tooltipTriggerList].map(tooltipTriggerEl => new Tooltip(tooltipTriggerEl));
  }, []);


  const [invoicingDetails, setInvoicingDetails] = useState({
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postalCode: '',
    country: '',
    state: '',
    taxId: '',
    taxIdType: 'eu_vat',
  });
  
  const [error, setError] = useState('');
  
  const handleNotificationChange = (type) => {
    setNotificationSettings((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  useEffect(() => {
    const fetchNotificationSettings = async () => {
      try {
        const res = await api.get(`/user/${user}/notification-settings`);
        if (res.status === 200) {
          setEmailNotificationsEnabled(res.data.notificationSettings.emailNotificationsEnabled);
          setNotificationSettings(res.data.notificationSettings);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    fetchNotificationSettings();
  }, [user]);

  const showTempNotification = (message, type = 'success') => {
    const id = showNotification(message, type);
    setTimeout(() => removeNotification(id), 4000);
  };

  const fetchNetworkHealth = async () => {
    try {
      const response = await api.get('/linkedin/network-health');
      setNetworkHealth(response.data || null);
    } catch (error) {
      console.error('Failed to fetch managed network health:', error);
      setNetworkHealth(null);
    }
  };

  useEffect(() => {
    fetchNetworkHealth();
  }, []);

  const saveNotificationSettings = async () => {
    setSavingNotifications(true);
    try {
      await api.post('/user/notification-settings', {
        email: user,
        emailNotificationsEnabled,
        notificationSettings,
      });
      setSavedNotifications(true);
      showTempNotification(t('settings.notificationsUpdated'), 'success');
    } catch (error) {
      console.error('Error saving notification settings:', error);
      showTempNotification(t('settings.errorUpdatingNotifications'), 'danger');
    } finally {
      setSavingNotifications(false);
      setTimeout(() => setSavedNotifications(false), 2000);
    }
  };
  



  useEffect(() => {
    // Get query parameters from the URL
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');
    
    if (tab) {
      setActiveTab(tab); // Set the active tab based on the query param
    }
  }, [location]);

  const getCurrentTimezoneOffset = () => {
    const date = new Date();
    const janOffset = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
    const julOffset = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();
    return janOffset === julOffset ? janOffset : (janOffset > julOffset ? -1 : 1);
  };
  
  // Declare the function normally
function determineEffectiveTimezone() {
  const offset = getCurrentTimezoneOffset();
  if (offset === -120) { // UTC+2
    return 'UTC+2';
  } else if (offset === -60) { // UTC+1
    return 'UTC+1';
  }
  return 'UTC'; // Default case
}

// Then use it in your state initialization
const [timezone, setTimezone] = useState(determineEffectiveTimezone());


const timezones = [
  { value: 'UTC-12', label: 'UTC-12', cities: ['Baker Island'] },
  { value: 'UTC-11', label: 'UTC-11', cities: ['American Samoa'] },
  { value: 'UTC-10', label: 'UTC-10', cities: ['Hawaii', 'Honolulu'] },
  { value: 'UTC-9', label: 'UTC-9', cities: ['Alaska'] },
  { value: 'UTC-8', label: 'UTC-8', cities: ['Los Angeles', 'San Francisco'] },
  { value: 'UTC-7', label: 'UTC-7', cities: ['Denver', 'Phoenix'] },
  { value: 'UTC-6', label: 'UTC-6', cities: ['Chicago', 'Houston'] },
  { value: 'UTC-5', label: 'UTC-5', cities: ['New York', 'Toronto'] },
  { value: 'UTC-4', label: 'UTC-4', cities: ['Caracas', 'Santiago'] },
  { value: 'UTC-3', label: 'UTC-3', cities: ['Brasília', 'Buenos Aires'] },
  { value: 'UTC-2', label: 'UTC-2', cities: ['South Georgia'] },
  { value: 'UTC-1', label: 'UTC-1', cities: ['Azores'] },
  { value: 'UTC', label: 'UTC', cities: ['London', 'Reykjavik'] },
  { value: 'UTC+1', label: 'UTC+1', cities: ['Berlin', 'Paris', 'Amsterdam'] },
  { value: 'UTC+2', label: 'UTC+2', cities: ['Amsterdam (during CEST)', 'Athens', 'Cairo'] },
  { value: 'UTC+3', label: 'UTC+3', cities: ['Moscow', 'Nairobi'] },
  { value: 'UTC+4', label: 'UTC+4', cities: ['Dubai', 'Baku'] },
  { value: 'UTC+5:30', label: 'UTC+5:30', cities: ['New Delhi', 'Mumbai'] },
  { value: 'UTC+8', label: 'UTC+8', cities: ['Beijing', 'Shanghai'] },
  { value: 'UTC+9', label: 'UTC+9', cities: ['Tokyo', 'Osaka'] },
  // Add more timezones as needed
];


const sortedCountriesLocation = [...countriesLocation].sort((a, b) => a.name.localeCompare(b.name));


useEffect(() => {
  const fetchSettings = async () => {
    try {
      const userResponse = await api.get(`/user/user`);
      const currentUser = userResponse.data.user;

      // Set other user details...
      setCurrentUser(currentUser || '');
      setCountry(currentUser.country || '');
      setTimezone(currentUser.timezone || '');
      setProfileImage(currentUser.profileImageUrl || '');
      setConnectionLimit(currentUser.connectionLimit || 100);
      setProfileViewLimit(currentUser.profileViewLimit || '');
      setMessagesLimit(currentUser.messagesLimit || '');

      // Set language from API response only if it's not already set
      const userLanguage = currentUser.language || 'en';
      if (!language) {
      setLanguage(userLanguage);
      i18n.changeLanguage(userLanguage);
    }
      
      const orgResponse = await api.get(`/user/organizations/${currentUser.organizationId}`);
      setInvoicingDetails(orgResponse.data.invoicingDetails || {});
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError(`${t('errors.fetchSettings')} ${error.response?.data?.error || t('errors.unknown')}`);
    }
  };

  fetchSettings();
}, [t, i18n]); // LinkedIn login email must not retrigger full settings fetch




  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
  
    try {
      // Upload image to backend (which will forward it to Cloudinary and update DB)
      const response = await api.post('/user/upload-profile-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      const imageUrl = response.data.imageUrl;
      setProfileImage(imageUrl);  // Set the profile image URL in the frontend
      setError('');  // Clear error
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(t('errors.uploadImage'));
    }
  };
  
  const handleSaveGeneral = async () => {
    setSavingGeneral(true);
    try {
      await new Promise(res => setTimeout(res, 300));
      await api.put('/user/general', { profileImageUrl: profileImage, language });
      i18n.changeLanguage(language);
      setUnsavedChanges(false);
      showTempNotification(t('settings.generalSettingsSaved'), 'success');
      setSavedGeneral(true);
    } catch {
      showTempNotification(t('errors.saveSettings'), 'danger');
    } finally {
      setSavingGeneral(false);
      setTimeout(() => setSavedGeneral(false), 2000);
    }
  };
  



  const handleSaveLocationClick = () => {
    // Open the modal to inform the user
    setShowModal(true);
  };

  
  const handleSaveLocation = async () => {
    setSavingLocation(true);
    try {
      const response = await api.put('/user/location', { country, timezone });
      setShowModal(false);
      setSavedLocation(true);
      setUnsavedChanges(false);

      if (response.data?.countryChangeRequested) {
        const activeCountry = response.data.currentCountry || currentUser?.country || '';
        setCountry(activeCountry);
        setCurrentUser((previous) => ({ ...(previous || {}), country: activeCountry, timezone: response.data.timezone || timezone }));
        showTempNotification(
          t('settings.countryChangeRequested', {
            defaultValue: 'Country change requested. Support will update your managed connection; the current connection remains active until then.',
          }),
          'info'
        );
      } else {
        const activeCountry = response.data?.updateData?.country || country;
        setCountry(activeCountry);
        setCurrentUser((previous) => ({ ...(previous || {}), country: activeCountry, timezone }));
        showTempNotification(t('settings.locationSettingsSaved'), 'success');
      }

      await fetchNetworkHealth();
    } catch (error) {
      console.error('Error saving location settings:', error);
      setError(t('errors.saveSettings'));
    } finally {
      setSavingLocation(false);
      setTimeout(() => setSavedLocation(false), 2000);
    }
  };
  
  
  


  // Settings.jsx – inside component
  const beginLinkedInLogin = async () => {
    setSavingCookie(true);
    try {
      // The backend owns the account identity and managed connection. The browser
      // only submits the LinkedIn credentials; no proxy details are user-facing.
      const { data } = await api.post('/linkedin/start-login', {
        email,
        password,
      });

      if (data.success && data.twoFARequired) {
        setSessionId(data.sessionId);
        setShow2FAModal(true);   // Now ask for the code
      } else if (data.success && data.twoFARequired === false) {
        // Cookie is already saved (no 2FA path)
        showTempNotification(t('settings.linkedInCookieSaved'), 'success');
        await fetchNetworkHealth();
      } else {
        throw new Error(data.error || 'Login failed');
      }
    } catch (e) {
      console.error('start-login failed', e);
      showTempNotification(t('errors.saveCookie'), 'danger');
    } finally {
      setSavingCookie(false);
    }
  };

  const submit2FA = async () => {
    if (!sessionId || pin.trim().length !== 6) return;
    setSavingCookie(true);
    try {
      const { data } = await api.post('/linkedin/complete-2fa', {
        sessionId,
        twoFACode: pin.trim(),
      });
      if (data.success) {
        setShow2FAModal(false);
        setSessionId(null);
        setPin('');
        showTempNotification(t('settings.linkedInCookieSaved'), 'success');
        await fetchNetworkHealth();
      } else {
        throw new Error(data.error || '2FA failed');
      }
    } catch (e) {
      console.error('complete-2fa failed', e);
      showTempNotification(t('errors.saveCookie'), 'danger');
    } finally {
      setSavingCookie(false);
    }
  };





// nciljfkanajefieifaclmfhaijkkpojn

const handleOpenExtensionPopup = () => {
  const EXT_ID = 'nciljfkanajefieifaclmfhaijkkpojn';
  const userEmail = Userfront.user.email;

  // Open the extension popup.html and pass the email as query param
  const popupUrl = `chrome-extension://${EXT_ID}/popup.html?email=${encodeURIComponent(userEmail)}`;
  window.open(popupUrl, '_blank', 'width=400,height=600');
};






useEffect(() => {
  if (show2FAModal && waitingForPin) {
    const timer = setTimeout(() => setWaitingForPin(false), 5000); // Simulate delay
    return () => clearTimeout(timer);
  }
}, [show2FAModal, waitingForPin]);

  

  // Function to redirect to the location tab
  const redirectToLocationTab = () => {
    setActiveTab('location');
    setShowLocationModal(false);  // Close the modal
  };


  const handleSaveLimits = async () => {
    setSavingLimits(true);
    try {
      await new Promise(res => setTimeout(res, 300));
      await api.put('/user/limits', { email: user, connectionLimit, profileViewLimit, messagesLimit });
      setUnsavedChanges(false);
      showTempNotification(t('settings.limitsSaved'), 'success');
      setSavedLimits(true);
    } catch {
      showTempNotification(t('errors.saveLimits'), 'danger');
    } finally {
      setSavingLimits(false);
      setTimeout(() => setSavedLimits(false), 2000);
    }
  };

  const showSaveGeneral = unsavedChanges || savingGeneral || savedGeneral;
  const showSaveLimits = unsavedChanges || savingLimits || savedLimits;
  

  return (
    <div className="border-t border-gray-200">
      <div className="min-h-screen bg-gray-50 transition-all space-y-6 px-2 pt-2 sm:px-3 md:px-4 xl:px-8 mb-6">
        <div className="space-y-6 max-w-[1600px] mx-auto px-2 sm:px-3 md:px-4 xl:px-8 pt-2">
          <h2 className="text-2xl font-semibold text-gray-800">{t('settings.pageHeader')}</h2>

            {/* Tabs */}
            <div className="flex space-x-4 border-b border-gray-200 mb-6">
              <button
                className={`px-4 py-2 font-medium text-sm border-b-2 transition ${
                  activeTab === 'account'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('account')}
              >
                {t('settings.myAccount')}
              </button>
              <button
                className={`px-4 py-2 font-medium text-sm border-b-2 transition ${
                  activeTab === 'campaigns'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('campaigns')}
              >
                {t('settings.campaigns')}
              </button>
              <button
                className={`px-4 py-2 font-medium text-sm border-b-2 ${
                  activeTab === 'email'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500'
                }`}
                onClick={() => setActiveTab('email')}
              >
                Channels
              </button>

              <button
                className={`px-4 py-2 font-medium text-sm border-b-2 transition ${
                  activeTab === 'integrations'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('integrations')}
              >
                {t('settings.integrations')}
              </button>
            </div>

            <div className="Settings">


                {/* Account Tab */}
                {activeTab === 'account' && (
                  <div className="space-y-8">
                    {/* Profile Header */}
                    <div className="flex items-center bg-gray-100 rounded-xl p-6">
                      <div className="text-center mr-6">
                        <img
                          src={profileImage || '/default-avatar.png'}
                          alt="Profile"
                          className="w-20 h-20 object-cover rounded-full border"
                        />
                        <input
                          id="profileImageInput"
                          type="file"
                          className="hidden"
                          onChange={handleProfileImageChange}
                          accept="image/*"
                        />
                        <button
                          className="text-sm text-blue-600 underline mt-2"
                          onClick={() => document.getElementById('profileImageInput').click()}
                        >
                          {t('settings.changeProfileImage')}
                        </button>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold">{currentUser?.name || t('settings.namePlaceholder')}</h4>
                        <p className="text-sm text-gray-500">{currentUser?.role || 'User'}</p>
                      </div>
                    </div>

                    {/* Account and Password */}
                    <div>
                      <h5 className="text-base font-semibold mb-2">{t('settings.accountAndSecurity')}</h5>
                      <label className="block text-sm mb-1" htmlFor="email">{t('settings.email')}</label>
                      <input
                        type="email"
                        id="email"
                        value={user}
                        disabled
                        className="w-full rounded-md border-gray-300 text-sm px-3 py-2 bg-gray-100"
                      />
                      <a href="/reset-password" className="inline-block mt-3 text-blue-600 text-sm underline">
                        {t('settings.resetPassword')}
                      </a>
                    </div>

                    {/* User Preferences */}
                    <div>
                      <h5 className="text-base font-semibold mb-2">{t('settings.userPreferences')}</h5>
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-1" htmlFor="language">
                          {t('settings.language')}
                          <i className="fas fa-info-circle text-xs text-gray-400 ml-1" title={t('tooltips.language')}></i>
                        </label>
                        <select
                          id="language"
                          value={language}
                          onChange={handleLanguageChange}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="en">{t('languages.english')}</option>
                          <option value="nl">{t('languages.dutch')}</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1" htmlFor="timezone">
                          {t('settings.timezone')}
                          <i className="fas fa-info-circle text-xs text-gray-400 ml-1" title={t('tooltips.timezone')}></i>
                        </label>
                        <select
                          id="timezone"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          value={timezone}
                          onChange={(e) => handleChangeWithTracking(setTimezone)(e.target.value)}
                        >
                          {!timezone && <option value="">{t('settings.selectTimezone')}</option>}
                          {timezones.map((zone) => (
                            <option key={zone.value} value={zone.value}>
                              {zone.label} ({zone.cities.join(', ')})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mt-4 flex items-center gap-2">
                        <button
                          className="bg-blue-600 text-white px-4 py-2 text-sm rounded-md"
                          onClick={handleSaveGeneral}
                          disabled={savingGeneral}
                        >
                          {savingGeneral ? (
                            <span className="spinner-border spinner-border-sm" role="status" />
                          ) : t('actions.saveGeneralSettings')}
                        </button>
                        {savedGeneral && <span className="text-green-600 text-sm">{t('actions.saved')}</span>}
                      </div>
                    </div>

                    {/* Managed network settings */}
                    <div>
                      <h5 className="text-base font-semibold mb-2">{t('settings.networkSettings')}</h5>
                      <p className="text-sm text-gray-600 mb-3">
                        {t('settings.managedNetworkExplanation', {
                          defaultValue: 'Choose your country. Leadgenflow configures and manages the LinkedIn connection automatically.',
                        })}
                      </p>
                      <label className="block text-sm mb-1" htmlFor="country">{t('settings.country')}</label>
                      <select
                        id="country"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        {!country && <option value="">{t('settings.selectCountry')}</option>}
                        {sortedCountriesLocation.map((c) => (
                          <option key={c.alpha3} value={c.id}>{c.name}</option>
                        ))}
                      </select>

                      {networkHealth && (
                        <div className={`mt-3 rounded-md border px-3 py-2 text-sm ${
                          networkHealth?.health?.supportRequired
                            ? 'border-amber-200 bg-amber-50 text-amber-800'
                            : networkHealth?.health?.healthy
                              ? 'border-green-200 bg-green-50 text-green-800'
                              : 'border-gray-200 bg-gray-50 text-gray-700'
                        }`}>
                          {!networkHealth.configured
                            ? t('settings.networkNotConfigured', {
                                defaultValue: 'Choose a country to configure your connection.',
                              })
                            : networkHealth?.health?.supportRequired
                              ? t('settings.networkSupportRequired', {
                                  defaultValue: 'We need to fix your managed connection. A support request has already been created.',
                                })
                              : networkHealth?.health?.healthy
                                ? t('settings.networkReady', { defaultValue: 'Connection is ready.' })
                                : ['degraded', 'unavailable'].includes(networkHealth?.health?.state)
                                  ? t('settings.networkTemporaryIssue', {
                                      defaultValue: 'Temporary connection issue. Leadgenflow will retry automatically.',
                                    })
                                  : t('settings.networkChecking', { defaultValue: 'Connection is being checked.' })}
                        </div>
                      )}

                      <div className="mt-4 flex items-center gap-2">
                        <button
                          className="bg-blue-600 text-white px-4 py-2 text-sm rounded-md"
                          onClick={handleSaveLocationClick}
                          disabled={savingLocation || !country}
                        >
                          {savingLocation ? (
                            <span className="spinner-border spinner-border-sm" role="status" />
                          ) : t('actions.saveLocationSettings')}
                        </button>
                        {savedLocation && <span className="text-green-600 text-sm">{t('actions.saved')}</span>}
                      </div>
                    </div>

                    <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">LinkedIn and email connections are managed from the Channels tab so connection health is visible in one place.</div>

                  </div>
                )}


                {activeTab === 'campaigns' && (
                  <div>
                    <h2 className="text-xl font-semibold mb-2">{t('settings.manageDailyLimits')}</h2>
                    <p className="text-sm text-gray-600 mb-6">{t('settings.dailyLimitsInfo')}</p>

                    <div className="space-y-6 max-w-md">
                      {/* Connection Limit */}
                      <div>
                        <label htmlFor="connectionLimit" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.connectionRequestLimit')}
                        </label>
                        <div className="flex items-center space-x-4">
                          <input
                            id="connectionLimit"
                            type="range"
                            min="0"
                            max="100"
                            step="10"
                            value={connectionLimit}
                            onChange={(e) => setConnectionLimit(e.target.value)}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                          <span className="w-10 text-right text-sm text-gray-500">{connectionLimit}</span>
                        </div>
                      </div>

                      {/* Profile View Limit */}
                      <div>
                        <label htmlFor="profileViewLimit" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.profileViewLimit')}
                        </label>
                        <div className="flex items-center space-x-4">
                          <input
                            id="profileViewLimit"
                            type="range"
                            min="0"
                            max="50"
                            step="10"
                            value={profileViewLimit}
                            onChange={(e) => setProfileViewLimit(e.target.value)}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                          <span className="w-10 text-right text-sm text-gray-500">{profileViewLimit}</span>
                        </div>
                      </div>

                      {/* Messages Limit */}
                      <div>
                        <label htmlFor="messagesLimit" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('settings.messagesLimit')}
                        </label>
                        <div className="flex items-center space-x-4">
                          <input
                            id="messagesLimit"
                            type="range"
                            min="0"
                            max="40"
                            step="10"
                            value={messagesLimit}
                            onChange={(e) => setMessagesLimit(e.target.value)}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                          <span className="w-10 text-right text-sm text-gray-500">{messagesLimit}</span>
                        </div>
                      </div>
                    </div>


                    <button
                      onClick={handleSaveLimits}
                      disabled={savingLimits}
                      className="btn btn-primary mt-6"
                    >
                      {savingLimits ? (
                        <span className="spinner-border spinner-border-sm" role="status" />
                      ) : t('actions.saveLimits')}
                    </button>
                    {savedLimits && <span className="text-green-600 text-sm ms-2">{t('actions.saved')}</span>}

                    {/* Notifications */}
                    <hr className="my-8" />
                    <h2 className="text-xl font-semibold mb-2">{t('settings.manageNotifications')}</h2>
                    <p className="text-sm text-gray-600 mb-6">{t('settings.notificationSettingsInfo')}</p>

                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="enableEmail"
                          className="form-checkbox h-4 w-4 text-blue-600"
                          checked={emailNotificationsEnabled}
                          onChange={() => setEmailNotificationsEnabled(!emailNotificationsEnabled)}
                        />
                        <label htmlFor="enableEmail" className="text-gray-700">
                          {t('settings.enableEmailNotifications')}
                        </label>
                      </div>

                      {emailNotificationsEnabled && (
                        <div className="border p-4 rounded bg-gray-50 space-y-2">
                          <h4 className="font-medium mb-2">{t('settings.selectNotificationTypes')}</h4>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="notifyConnection"
                              className="form-checkbox h-4 w-4 text-blue-600"
                              checked={notificationSettings.newConnection}
                              onChange={() => handleNotificationChange('newConnection')}
                            />
                            <label htmlFor="notifyConnection" className="text-gray-700">
                              {t('settings.newConnection')}
                            </label>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="notifyMessage"
                              className="form-checkbox h-4 w-4 text-blue-600"
                              checked={notificationSettings.newMessage}
                              onChange={() => handleNotificationChange('newMessage')}
                            />
                            <label htmlFor="notifyMessage" className="text-gray-700">
                              {t('settings.newMessage')}
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={saveNotificationSettings}
                      disabled={savingNotifications}
                      className="btn btn-primary mt-6"
                    >
                      {savingNotifications ? (
                        <span className="spinner-border spinner-border-sm" role="status" />
                      ) : t('settings.saveSettings')}
                    </button>
                    {savedNotifications && <span className="text-green-600 text-sm ms-2">{t('actions.saved')}</span>}
                  </div>
                )}


                {activeTab === 'email' && <ChannelConnections />}


                {activeTab === 'integrations' && (
                  <div>
                    <h2 className="text-xl font-semibold mb-2">{t('settings.integrations')}</h2>
                    <p className="text-sm text-gray-600 mb-6">{t('settings.integrationsInfo')}</p>

                    {/* Optional: read initialSelectedKey from URL, e.g., new URLSearchParams(location.search).get('integration') */}
                    <IntegrationsView t={t} />
                  </div>
                )}

              </div>
    
  




      {showModal && (
        <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('settings.locationWarningTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t('settings.locationWarningMessage')}</Form.Label>
            <Form.Text className="text-muted">
              {t('settings.locationSupportMessage')}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            {t('actions.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSaveLocation}>
            {t('actions.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
      )}


      {showLocationModal && (
      <Modal show={showLocationModal} onHide={() => setShowLocationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('settings.locationRequiredTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('settings.locationRequiredMessage')}</p>
          <p>{t('settings.locationSupportMessage')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLocationModal(false)}>
            {t('actions.cancel')}
          </Button>
          <Button variant="primary" onClick={redirectToLocationTab}>
            {t('actions.goToLocationSettings')}
          </Button>
        </Modal.Footer>
      </Modal>
      )}


      <Modal show={showUnsavedModal} onHide={cancelNavigation} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>{t('settings.unsavedChanges')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('settings.unsavedChangesMessage')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelNavigation}>{t('actions.cancel')}</Button>
          <Button variant="danger" onClick={confirmNavigation}>{t('actions.discardChanges')}</Button>
        </Modal.Footer>
      </Modal>


      <Modal show={show2FAModal} onHide={() => setShow2FAModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('settings.twoFactorAuth')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('settings.enterPinPrompt')}</p>
          <input
            className="form-control mb-3"
            type="text"
            placeholder="123456"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />
          <button className="btn btn-primary w-100" onClick={submit2FA} disabled={savingCookie || pin.trim().length !== 6}>
            {savingCookie ? <span className="spinner-border spinner-border-sm" role="status" /> : t('actions.submit')}
          </button>
        </Modal.Body>
      </Modal>

    </div>
  </div>
</div>
)}

export default Settings;
